import type { MapFrameTransform } from "../api/rest";

/**
 * Pure geometry for the map calibration editor (ADR 258).
 *
 * A calibration is the 2-D similarity {tx, ty, theta, scale} that carries a map's own (kit)
 * frame into the warehouse frame: `w = t + s·R(theta)·p`. Every gesture the editor offers is
 * expressed as a change to that similarity rather than as a separate view transform, so what
 * the operator sees on screen is exactly what gets saved.
 *
 * Kept free of React and of the canvas so the arithmetic can be tested directly — the drawing
 * code has no decisions left in it.
 */

export const IDENTITY: MapFrameTransform = { tx: 0, ty: 0, theta: 0, scale: 1 };

export type Point = readonly [number, number];
export interface Bounds { minX: number; maxX: number; minY: number; maxY: number; }

/** Screen mapping: pixels-per-metre plus the pixel offset of world (0,0). */
export interface View { ppm: number; offsetX: number; offsetY: number; }

export function applyTransform(t: MapFrameTransform, x: number, y: number): Point {
  const c = Math.cos(t.theta), s = Math.sin(t.theta);
  return [t.tx + t.scale * (c * x - s * y), t.ty + t.scale * (s * x + c * y)];
}

/** True when the transform places geometry unchanged — the server's "uncalibrated". */
export function isIdentity(t: MapFrameTransform): boolean {
  return t.tx === 0 && t.ty === 0 && t.theta === 0 && t.scale === 1;
}

export function boundsOf(polygons: readonly (readonly Point[])[]): Bounds | null {
  let minX = Infinity, maxX = -Infinity, minY = Infinity, maxY = -Infinity;
  for (const poly of polygons) {
    for (const [x, y] of poly) {
      if (!Number.isFinite(x) || !Number.isFinite(y)) continue;
      if (x < minX) minX = x;
      if (x > maxX) maxX = x;
      if (y < minY) minY = y;
      if (y > maxY) maxY = y;
    }
  }
  return minX === Infinity ? null : { minX, maxX, minY, maxY };
}

export function unionBounds(a: Bounds | null, b: Bounds | null): Bounds | null {
  if (!a) return b;
  if (!b) return a;
  return {
    minX: Math.min(a.minX, b.minX), maxX: Math.max(a.maxX, b.maxX),
    minY: Math.min(a.minY, b.minY), maxY: Math.max(a.maxY, b.maxY),
  };
}

/**
 * Fit `bounds` into a `width × height` viewport with `pad` pixels of margin. Y is NOT
 * flipped — deliberately (ADR 311): the stored 2D frame is image-like, +y = South
 * (ADR 25 "+Z = South", ADR 189), so world y maps straight down the canvas and the
 * editor reads north-up like the 3D scene seen from above. The old y-up flip here
 * showed every warehouse N-S mirrored against /live — self-consistently, so the saved
 * calibration was still correct, but the picture read wrong.
 */
export function fitView(bounds: Bounds | null, width: number, height: number, pad = 24): View {
  if (!bounds || width <= 0 || height <= 0) return { ppm: 1, offsetX: width / 2, offsetY: height / 2 };
  const w = Math.max(bounds.maxX - bounds.minX, 1e-6);
  const h = Math.max(bounds.maxY - bounds.minY, 1e-6);
  const ppm = Math.min((width - 2 * pad) / w, (height - 2 * pad) / h);
  const cx = (bounds.minX + bounds.maxX) / 2, cy = (bounds.minY + bounds.maxY) / 2;
  return { ppm, offsetX: width / 2 - cx * ppm, offsetY: height / 2 - cy * ppm };
}

/** Zoom band, pixels per metre. A warehouse runs 10–500 m across, so the floor shows a
 *  500 m building in 50 px and the ceiling shows 10 cm across the canvas — past either end
 *  there is nothing left to look at. */
export const MIN_PPM = 0.1;
export const MAX_PPM = 5000;

const clamp = (v: number, lo: number, hi: number) => Math.min(hi, Math.max(lo, v));

/**
 * Zoom about a SCREEN point (ADR 323), keeping whatever world position sits under it fixed —
 * the cursor for a wheel, the canvas centre for the buttons. Anchoring anywhere else makes
 * the thing you are inspecting slide off the moment you zoom in on it.
 *
 * The clamp is applied to the resulting `ppm` and the anchor solved with the factor that
 * SURVIVED it, so a wheel spun at the limit holds the anchor instead of drifting.
 */
export function zoomViewAt(v: View, factor: number, px: number, py: number): View {
  const ppm = clamp(v.ppm * factor, MIN_PPM, MAX_PPM);
  const k = ppm / v.ppm;
  return { ppm, offsetX: px - (px - v.offsetX) * k, offsetY: py - (py - v.offsetY) * k };
}

export function panView(v: View, dxPx: number, dyPx: number): View {
  return { ...v, offsetX: v.offsetX + dxPx, offsetY: v.offsetY + dyPx };
}

export function worldToScreen(v: View, x: number, y: number): Point {
  return [v.offsetX + x * v.ppm, v.offsetY + y * v.ppm];
}

export function screenToWorld(v: View, px: number, py: number): Point {
  return [(px - v.offsetX) / v.ppm, (py - v.offsetY) / v.ppm];
}

export function translateBy(t: MapFrameTransform, dx: number, dy: number): MapFrameTransform {
  return { ...t, tx: t.tx + dx, ty: t.ty + dy };
}

/**
 * Rotate the PLACED map by `delta` about a world pivot. Substituting `w' = c + R(δ)(w − c)`
 * into `w = t + s·R(θ)p` gives `θ' = θ + δ` and `t' = c + R(δ)(t − c)` — still a similarity,
 * so the gesture never produces something the column cannot store.
 */
export function rotateAbout(t: MapFrameTransform, delta: number, pivot: Point): MapFrameTransform {
  const [cx, cy] = pivot;
  const c = Math.cos(delta), s = Math.sin(delta);
  const dx = t.tx - cx, dy = t.ty - cy;
  return { tx: cx + c * dx - s * dy, ty: cy + s * dx + c * dy, theta: t.theta + delta, scale: t.scale };
}

/** Scale the placed map about a world pivot: `s' = f·s`, `t' = c + f(t − c)`. */
export function scaleAbout(t: MapFrameTransform, factor: number, pivot: Point): MapFrameTransform {
  const [cx, cy] = pivot;
  return {
    tx: cx + factor * (t.tx - cx), ty: cy + factor * (t.ty - cy),
    theta: t.theta, scale: t.scale * factor,
  };
}

/** Wrap to (−π, π] so the editor never shows 361° or −540° after repeated nudges. */
export function normalizeTheta(theta: number): number {
  const wrapped = ((theta + Math.PI) % (2 * Math.PI) + 2 * Math.PI) % (2 * Math.PI) - Math.PI;
  return wrapped === -Math.PI ? Math.PI : wrapped;
}

export const toDegrees = (rad: number): number => (rad * 180) / Math.PI;
export const toRadians = (deg: number): number => (deg * Math.PI) / 180;

/**
 * One paired address and how far apart the two maps put it (ADR 323). The RMS says a
 * calibration is 0,42 m out; this says WHERE — which is the difference between a number and
 * a diagnosis. A residual that is huge on three bays and nil everywhere else is a bad
 * address in the data; one spread evenly is a bad transform.
 */
export interface Residual { key: string; from: Point; to: Point; d: number; }

/** Every shared address, worst first. Unpaired keys are skipped, not scored as zero. */
export function residuals(
  edited: ReadonlyMap<string, Point>,
  reference: ReadonlyMap<string, Point>,
): Residual[] {
  const out: Residual[] = [];
  for (const [key, p] of edited) {
    const ref = reference.get(key);
    if (!ref) continue;
    out.push({ key, from: p, to: ref, d: Math.hypot(p[0] - ref[0], p[1] - ref[1]) });
  }
  out.sort((a, b) => b.d - a.d);
  return out;
}

/** RMS over already-computed residuals, metres. `NaN` (never 0) for no pairs: an
 *  unmeasurable alignment must not read as a perfect one. */
export function rmsOf(rs: readonly Residual[]): { rms: number; pairs: number } {
  if (rs.length === 0) return { rms: NaN, pairs: 0 };
  let sum = 0;
  for (const r of rs) sum += r.d * r.d;
  return { rms: Math.sqrt(sum / rs.length), pairs: rs.length };
}

/**
 * RMS distance between two maps' shared addresses, in metres — the same number the
 * auto-aligner reports, recomputed for whatever the operator has dragged to. Without it the
 * editor would be a way to make a calibration look right rather than be right.
 *
 * Defined through {@link residuals} so the score on screen and the segments drawn under it
 * can never be two different opinions about the same pairing.
 */
export function rmsAgainst(
  edited: ReadonlyMap<string, Point>,
  reference: ReadonlyMap<string, Point>,
): { rms: number; pairs: number } {
  return rmsOf(residuals(edited, reference));
}

/**
 * Correspondence key: the address with its level field blanked, so bay 00 of level 5 pairs
 * with bay 00 of level 2. Mirrors what the backend aligner keys on (ADR 243/244) — the
 * comparison is only meaningful between the keys a fit would actually pair, never between
 * address suffixes, which name the two rack sides across every bay.
 */
export function correspondenceKey(address: string, aisleLabel: string | null): string | null {
  if (!address) return null;
  const m = /^([A-Za-z]+)(\d*)-?(.*)$/.exec(address.trim());
  if (!m) return null;
  const aisle = aisleLabel ?? m[1];
  return `${aisle}|${m[3]}`;
}
